import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building, CheckCircle, ArrowRight, Briefcase, Factory, Store } from 'lucide-react';

const CommercialPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.35 }
  };

  const commercialBenefits = [
    'High traffic durability',
    'Low maintenance costs',
    'Professional appearance',
    'Stain and chemical resistant',
    'Improves lighting efficiency',
    'Long-term cost savings'
  ];

  const commercialTypes = [
    { icon: Store, title: 'Retail Spaces', description: 'Shopping centers, boutiques, and showrooms that require durable, attractive flooring' },
    { icon: Briefcase, title: 'Corporate Offices', description: 'Professional environments where appearance and maintenance matter' },
    { icon: Factory, title: 'Industrial Facilities', description: 'Warehouses and manufacturing spaces needing heavy-duty solutions' },
    { icon: Building, title: 'Hospitality Venues', description: 'Hotels, restaurants, and entertainment facilities' }
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="commercial-page">
      {/* Hero Video Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="commercial-hero-video"
        >
          <source src="https://customer-assets.emergentagent.com/job_concretepro/artifacts/ox60agx8_20250313_161022.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="max-w-4xl"
          >
            <Building className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              Commercial
              <span className="bg-gradient-to-r from-slate-300 to-slate-100 bg-clip-text text-transparent block">
                Solutions
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              Professional concrete polishing solutions designed for commercial environments. 
              Durable, cost-effective, and built to withstand high traffic demands.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-black text-white border-0 shadow-2xl"
                data-testid="hero-cta-button"
              >
                Get Commercial Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Commercial Types */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 py-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-800 dark:text-slate-100">
              Commercial Applications
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              From retail showrooms to industrial warehouses, our concrete solutions deliver 
              performance and aesthetics that meet commercial demands.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {commercialTypes.map((type, index) => (
              <motion.div
                key={type.title}
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-6 text-center">
                    <type.icon className="w-12 h-12 text-slate-600 dark:text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2 text-slate-800 dark:text-slate-100">
                      {type.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {type.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Benefits */}
      <section className="py-12 bg-slate-50 dark:bg-slate-900" data-testid="commercial-benefits">
        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-800 dark:text-slate-100">
                Why Choose Polished Concrete for Commercial Spaces?
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                Commercial environments demand flooring that can handle heavy foot traffic, equipment loads, 
                and frequent cleaning while maintaining a professional appearance. Our polished concrete 
                solutions deliver all this and more.
              </p>
              <div className="grid gap-4">
                {commercialBenefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-200 font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeInUp}>
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_concretepro/artifacts/xm9u8qo0_20250313_161420.jpg"
                  alt="Commercial polished concrete in fitness facility"
                  className="rounded-lg shadow-lg w-full h-[400px] object-cover"
                  data-testid="commercial-showcase-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-lg" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-bold">Premium Commercial Installation</h3>
                  <p className="text-sm opacity-90">Four wheel drive parts & accessories showroom</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Commercial Project Video */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 py-8">
          <motion.div {...fadeInUp} className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-800 dark:text-slate-100">
              Commercial Project Showcase
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Watch our commercial polished concrete installation process and see the professional results
            </p>
          </motion.div>
          
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <Card className="overflow-hidden shadow-lg">
              <div className="relative aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/N53248vBABc?hd=1&vq=hd1080&rel=0&modestbranding=1"
                  title="Commercial Polished Concrete Project Showcase"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="commercial-project-video"
                />
              </div>
              <CardContent className="p-6 text-center bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700">
                <h3 className="text-xl font-semibold mb-2 text-slate-800 dark:text-slate-100">
                  Commercial Polished Concrete Installation
                </h3>
                <p className="text-slate-600 dark:text-slate-300">
                  See our professional team transform commercial spaces with high-quality polished concrete
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 py-8 text-center">
          <motion.div {...fadeInUp}>
            <Card className="p-12 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-100 dark:to-slate-200 text-white dark:text-slate-900">
              <Building className="w-16 h-16 mx-auto mb-6 text-slate-300 dark:text-slate-600" />
              <h2 className="text-3xl font-bold mb-4">
                Ready to Transform Your Commercial Space?
              </h2>
              <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
                Let our commercial specialists design a concrete solution that meets your 
                business needs, budget, and timeline requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-black text-white border-0"
                    data-testid="commercial-cta-contact"
                  >
                    Get Commercial Quote <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white hover:text-slate-900 dark:border-slate-900 dark:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-white"
                    data-testid="commercial-cta-services"
                  >
                    View All Services
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CommercialPage;